import React, { useState, useEffect } from 'react';
import { TreePine, MapPin, Calendar, BarChart3, Tent, Home, Car, Backpack } from 'lucide-react';

// Reusable Carousel Component for individual card rotation
const StatsCarousel = ({ title, subtitle, stats }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % stats?.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [stats?.length]);

  const goToCard = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="mb-16">
      {/* Carousel Header */}
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-gray-900 mb-2">
          {title}
        </h3>
        <p className="text-gray-600">
          {subtitle}
        </p>
      </div>

      {/* Carousel Container */}
      <div className="relative max-w-sm mx-auto">
        {/* Card Display */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}>

            {stats?.map((item, index) => {
              const IconComponent = item?.icon;
              return (
                <div key={index} className="w-full flex-shrink-0 px-3">
                  <div className="bg-white rounded-xl shadow-lg p-6 transform hover:scale-105 transition-transform duration-200 border-2">
                    <div className="flex items-center justify-center mb-4">
                      <div className={`p-3 rounded-lg ${item?.bgColor}`}>
                        <IconComponent className={`h-6 w-6 ${item?.color}`} />
                      </div>
                    </div>
                    <div className="text-center space-y-1.5">
                      <div className="text-3xl font-bold text-gray-900">
                        {item?.value}
                      </div>
                      <div className="text-base font-medium text-gray-700">
                        {item?.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {item?.description}
                      </div>
                    </div>
                  </div>
                </div>);

            })}
          </div>
        </div>

        {/* Carousel Indicators */}
        <div className="flex justify-center gap-2 mt-6">
          {stats?.map((_, index) =>
          <button
            key={index}
            onClick={() => goToCard(index)}
            className={`h-2 rounded-full transition-all duration-300 ${
            currentIndex === index ?
            'w-6 bg-green-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`
            }
            aria-label={`Go to card ${index + 1}`} />

          )}
        </div>
      </div>
    </div>);

};

const StateParkStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Static data arrays defined at the top
  const mainStats = [
  {
    icon: TreePine,
    value: '9,800+',
    label: 'Managed State Units',
    description: 'State Parks, Beaches, etc.',
    color: 'text-green-600',
    bgColor: 'bg-green-100'
  },
  {
    icon: MapPin,
    value: '20M+',
    label: 'Total Acres',
    description: 'Encompassing protected land',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100'
  },
  {
    icon: BarChart3,
    value: '50',
    label: 'States Covered',
    description: 'Across the United States',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100'
  },
  {
    icon: Calendar,
    value: 'Since 1885',
    label: 'Conservation Legacy',
    description: 'First state park in New York',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100'
  }];


  const campingStats = [
  {
    icon: Tent,
    value: '≈217,000',
    label: 'Total Campsites',
    description: 'Available across all parks',
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-100'
  },
  {
    icon: Car,
    value: '~120,000',
    label: 'Car Camping Sites',
    description: 'Improved/developed campsites',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100'
  },
  {
    icon: Backpack,
    value: '~95,000',
    label: 'Primitive Sites',
    description: 'Backpacking & primitive camping',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100'
  },
  {
    icon: Home,
    value: '8,300+',
    label: 'Lodging Units',
    description: 'Cabins, lodges & resort units',
    color: 'text-rose-600',
    bgColor: 'bg-rose-100'
  }];


  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);

        // Static data - no database fetch needed for managed units
        const data = {
          total_acreage: 20000000,
          total_states: 50,
          oldest_park_year: 1885,
          total_campsites: 217000,
          car_camping_sites: 120000,
          primitive_sites: 95000,
          total_campgrounds: 2750,
          lodging_units: 8300
        };
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching state park statistics:', err);
        setError('Failed to load state park statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  // NOW conditional returns are safe - all hooks have been called
  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              State Park Statistics
            </h2>
            <div className="animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
          
          <div className="flex justify-center">
            <div className="w-full max-w-md">
              <div className="bg-white rounded-lg shadow-md p-8 animate-pulse">
                <div className="flex items-center justify-center mb-4">
                  <div className="h-12 w-12 bg-gray-300 rounded"></div>
                </div>
                <div className="h-10 bg-gray-300 rounded mb-2"></div>
                <div className="h-6 bg-gray-300 rounded w-3/4 mx-auto mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  if (error) {
    return (
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              State Park Statistics
            </h2>
            <div className="text-red-600 bg-red-50 border border-red-200 rounded-lg p-4 inline-block">
              {error}
            </div>
          </div>
        </div>
      </section>);

  }

  return (
    <section className="bg-gradient-to-br from-green-50 to-blue-50 py-3">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-[00px]">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-6 flex items-center justify-center gap-2">
            <span className="text-4xl">🇺🇸</span>
            Discover America's State Parks
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-4">
            Explore the vast network of protected natural areas across the United States. 
            From towering mountains to pristine coastlines, our state parks offer endless adventures.
          </p>
          <div className="text-sm text-gray-500 italic">
            America's state park system has been protecting natural treasures since 1885
          </div>
        </div>

        {/* Two Separate Carousels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* State Park Overview Carousel */}
          <StatsCarousel
            title="State Park Overview"
            subtitle="Discover America's Natural Heritage"
            stats={mainStats} />


          {/* Camping & Lodging Carousel */}
          <StatsCarousel
            title="Camping & Lodging"
            subtitle="Your Perfect Outdoor Experience"
            stats={campingStats} />

        </div>

        {/* Additional Key Facts */}
        <div className="bg-white rounded-xl shadow-lg mt-16 p-2.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                ~2,500-3,000
              </div>
              <div className="text-lg font-medium text-gray-900 mb-1">
                Distinct Campground Areas
              </div>
              <div className="text-sm text-gray-600">
                State park areas with campground facilities
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                139+ Years
              </div>
              <div className="text-lg font-medium text-gray-900 mb-1">
                Conservation Heritage
              </div>
              <div className="text-sm text-gray-600">
                Protecting America's natural beauty since 1885
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default StateParkStats;